import { RootState, store } from "@app/store";
import { setDropOffs, setPickup } from "@app/store/marker.slice";
import { setOrderDriver, setOrderStatus, setRealETA, setStage, setOrderPickup, setOrderDropoffs, setRemainingQueuePosition, setIsLastDropOff } from "@app/store/order.slice";
import { setService, setVehicle } from "@app/store/service.slice";
import {
  CustomerOrderUpdated
} from "@app/types/socket";
import { OrderDeliveryAddress, OrderDropoffAddress } from "@app/types/orders";
import { DefaultEventsMap } from "@socket.io/component-emitter";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import io, { Socket } from "socket.io-client";
import packageJson from "../../package.json";
import { computeDropoffQueueState, mapSocketDropoff, mapSocketPickup } from "./socketOrderMapping";

type SocketIoInput = {};
export const useSocketIo = ({}: SocketIoInput) => {
  const socketServerUrl = useSelector(
    (state: RootState) => state.setting.socketServerUrl
  );
  const customerId = useSelector(
    (state: RootState) => state.customer.customerId
  );
  const language = useSelector((state: RootState) => state.setting.language);

  console.log({ socketServerUrl, customerId, language });

  let socket: Socket<DefaultEventsMap, DefaultEventsMap>;
  const [isConnected, setIsConnected] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (socketServerUrl && customerId && language) {
      socket = io(socketServerUrl, {
        secure: true,
        query: {
          customerId: customerId,
          language: language || "en",
          apiVersion: "v1",
          apiSource: "web",
          appVersionCode: packageJson.version,
          appVersionName: packageJson.version,
        },
        reconnection: true,
        reconnectionAttempts: 1000,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 4000,
        autoConnect: true,
        transports: ["websocket"],
      });
      socket.on("connect", () => {
        setIsConnected(true);
        console.log("Socket Connect");
      });

      socket.on("disconnect", () => {
        setIsConnected(false);
        console.log("Socket Disconnect");
      });

      socket.on("customer:order:updated",
        (data: CustomerOrderUpdated) => {
          console.log("customer:order:updated : ", data);
          // Read the order id straight from the store rather than the
          // `orderId` selector value closed over when this effect last ran
          // (the effect's deps don't include `orderId`, so that value can
          // go stale — `store.getState()` never can).
          const orderState = store.getState().order;
          if (data.data.id === orderState.orderId) {
            dispatch(
              setOrderStatus({
                status: data.data.status,
                statusDescription: data.data.statusDescription,
              })
            );

            if (data.data.driver) {
              dispatch(
                setOrderDriver({
                  avatarPhoto: data.data.driver.avatarPhoto,
                  name: data.data.driver.name,
                  phone: data.data.driver.phone,
                  rate: data.data.driver.rate,
                  vehicle: {
                    plate: data.data.driver.vehicle.plate,
                    color: data.data.driver.vehicle.color,
                    model: data.data.driver.vehicle.model,
                  },
                  customFields: data.data.driver.customFields,
                  customFieldDefinitions: data.data.driver.customFieldDefinitions
                })
              );
            }

            if (data.data.service) {
              let options = [];
              options = data.data.service.options.map((o) => ({
                title: o.title,
                type: o.type,
                dataTitle: o.dataTitle,
              }));

              dispatch(
                setService({
                  title: data.data.service.title,
                  icon: data.data.service.icon,
                  options: options,
                })
              );
            }

            if (data.data.vehicleType) {
              let options = [];
              options = data.data.vehicleType.options.map((o) => ({
                title: o.title,
                type: o.type,
                dataTitle: o.dataTitle,
              }));

              dispatch(
                setVehicle({
                  title: data.data.vehicleType.title,
                  icon: data.data.vehicleType.icon,
                  options: options,
                })
              );
            }

            if (data.data.pickup && orderState.pickup) {
              dispatch(
                setOrderPickup(
                  mapSocketPickup(data.data.pickup, orderState.pickup, orderState.type, language)
                )
              );
            }

            let socketDropoffs: (OrderDropoffAddress | OrderDeliveryAddress)[] =
              data.data.dropoffs && data.data.dropoffs.length > 0 ? [...data.data.dropoffs] : [];
            if (data.data.dropoff) socketDropoffs.push(data.data.dropoff);
            if (data.data.delivery) socketDropoffs.push(data.data.delivery);

            if (socketDropoffs.length > 0 && orderState.dropoffs && orderState.dropoffs.length > 0) {

              if (orderState.isDropoffTracking) {
                const trackedDropoff = orderState.dropoffs[0];
                const updated = socketDropoffs.find((sd) => sd.id === trackedDropoff.id);

                if (updated) {
                  dispatch(
                    setOrderDropoffs([
                      mapSocketDropoff(updated, trackedDropoff, orderState.type, language),
                    ])
                  );
                }
              } else {
                const updatedDropoffs = orderState.dropoffs.map((d) => {
                  const updated = socketDropoffs.find((sd) => sd.id === d.id);
                  if (!updated) return d;
                  return mapSocketDropoff(updated, d, orderState.type, language);
                });
                dispatch(setOrderDropoffs(updatedDropoffs));
              }
            }

            if (
              data.data.type == "Ondemand" &&
              data.data.pickup &&
              data.data.dropoffs
            ) {
              dispatch(
                setPickup({
                  lat: data.data.pickup.coordinates[1],
                  lng: data.data.pickup.coordinates[0],
                  address: data.data.pickup.address,
                })
              );

              if (orderState.isDropoffTracking) {
                const trackedId = orderState.dropoffs?.[0]?.id;
                const trackedDropoff = data.data.dropoffs.find(
                  (d) => d.id === trackedId
                );

                if (trackedDropoff) {
                  dispatch(
                    setDropOffs([
                      {
                        lat: trackedDropoff.coordinates[1],
                        lng: trackedDropoff.coordinates[0],
                        address: trackedDropoff.address,
                      },
                    ])
                  );
                }
              } else {
                const dropoffs = data.data.dropoffs.map((d: any) => {
                  return {
                    lat: d.coordinates[1],
                    lng: d.coordinates[0],
                    address: d.address,
                  };
                });
                dispatch(setDropOffs(dropoffs));
              }
            }

            if (data.data.type == "Pickup" && data.data.pickup) {
              dispatch(
                setPickup({
                  lat: data.data.pickup.coordinates[1],
                  lng: data.data.pickup.coordinates[0],
                  address: data.data.pickup.address,
                })
              );
            }

            if (data.data.type == "Delivery" && data.data.dropoff) {
              dispatch(
                setDropOffs([
                  {
                    lat: data.data.dropoff.coordinates[1],
                    lng: data.data.dropoff.coordinates[0],
                    address: data.data.dropoff.address,
                  },
                ])
              );
            }

            // Always propagate realETA, including 0 — 0 is a meaningful
            // value (arrived / no more estimate), not "no update". Skipping
            // it left the UI showing the last non-zero countdown forever.
            dispatch(setRealETA(data.data.realETA));

            if(data.data.stage){
              dispatch(setStage(data.data.stage))
            }

            // The socket payload never carries pre-computed
            // remainingQueuePosition/isLastDropOff (unlike the Track
            // Drop-off REST response) — derive them the same way the server
            // does, from the raw dropoffs queue, identifying the tracked
            // dropoff by `id` (already loaded into `orderState.dropoffs[0]`
            // for Single Drop-off Tracking — see the merge above). Only
            // meaningful in that mode: outside it there's no single
            // "tracked" dropoff for these two fields to describe. `null`
            // means the tracked dropoff isn't in this payload's dropoffs
            // list — skip rather than error.
            if (orderState.isDropoffTracking) {
              const queueState = computeDropoffQueueState(data.data.dropoffs, orderState.dropoffs?.[0]?.id);
              if (queueState) {
                dispatch(setRemainingQueuePosition(queueState.remainingQueuePosition));
                dispatch(setIsLastDropOff(queueState.isLastDropOff));
              }
            }

          }
        }
      );

    }

    return () => {
      if (socket) {
        socket.off("connect");
        socket.off("disconnect");
        socket.off("pong");
        socket.off("customer:order:updated");
        socket.disconnect();
      }
    };
  }, [socketServerUrl, customerId, language]);

  console.log(isConnected);

  return { isConnected };
};
